// src/components/PublicHomePage.js - Updated to use existing NavBar and ProductGrid components
import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Button,
  Box,
  Alert,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Badge,
} from "@mui/material";
import {
  Login,
  ShoppingBagOutlined,
  Search,
  ShoppingCart,
} from "@mui/icons-material";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase/config";

// Import the same components used in HomePage
import NavBar from "./NavBar";
import Footer from "./Footer";
import FloatingCartButton from "./FloatingCartButton";
import Notifications from "./Notifications";
import UserMenu from "./UserMenu";
import ProductGrid from "./ProductGrid";
import { shuffleArray } from "../utils/arrayUtils";
import {
  ALL_CATEGORIES_LABEL,
  NAV_CATEGORIES,
  canonicalCategoryName,
  categoryMatchesFilter,
} from "../config/productCategories";
import CategoryIconStrip from "./CategoryIconStrip";
import CategoryComingSoonPanel from "./CategoryComingSoonPanel";
import {
  brandPalette as primaryBlue,
  brandGlassStyles as glassStyles,
  brandCardGlassStyles as cardGlassStyles,
  getBrandPageBackground,
  brandColors,
} from "../config/brandTheme";

const PublicHomePage = ({ user, onAuthRequired, onSignOut }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [authDialog, setAuthDialog] = useState(false);
  const [selectedItemForAuth, setSelectedItemForAuth] = useState(null);
  const [error, setError] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  // Search and Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(ALL_CATEGORIES_LABEL);
  const [categories] = useState(NAV_CATEGORIES);

  // Pagination states
  const [displayedItemsCount, setDisplayedItemsCount] = useState(8);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Modal states for consistency with HomePage
  const [selectedItemForModal, setSelectedItemForModal] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Mock cart for public users (empty)
  const [cart] = useState({ packages: [], items: [] });

  // Fetch active items
  const fetchItems = async () => {
    try {
      setItemsLoading(true);
      setError("");

      const itemsQuery = query(
        collection(db, "items"),
        where("isActive", "==", true)
      );
      const snapshot = await getDocs(itemsQuery);
      const itemsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setItems(itemsData);
    } catch (error) {
      console.error("Failed to fetch items for public view:", error);

      if (error.code === "permission-denied") {
        setError(
          "Database access restricted. Please check Firestore security rules."
        );
      } else if (error.code === "unavailable") {
        setError("Database temporarily unavailable. Please try again later.");
      } else {
        setError(`Failed to load items: ${error.message}`);
      }
    } finally {
      setItemsLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(price);
  };

  // Filter items based on search and category
  const getFilteredItems = () => {
    let filtered = items;

    if (selectedCategory !== ALL_CATEGORIES_LABEL) {
      filtered = filtered.filter((item) =>
        categoryMatchesFilter(item.category, selectedCategory)
      );
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(
        (item) =>
          (item.name && item.name.toLowerCase().includes(query)) ||
          (item.description &&
            item.description.toLowerCase().includes(query)) ||
          (item.brand && item.brand.toLowerCase().includes(query)) ||
          (item.category && item.category.toLowerCase().includes(query))
      );
    }

    // Shuffle the filtered results every time
    return shuffleArray(filtered);
  };

  const filteredItems = getFilteredItems();

  const itemCountByCategory = useMemo(() => {
    const counts = {};
    items.forEach((item) => {
      if (item.category) {
        const key = canonicalCategoryName(item.category);
        counts[key] = (counts[key] || 0) + 1;
      }
    });
    return counts;
  }, [items]);

  const categoryImageOverrides = useMemo(() => {
    const images = {};
    items.forEach((item) => {
      if (!item.category || !item.imageUrl) return;
      const key = canonicalCategoryName(item.category);
      if (!images[key]) images[key] = item.imageUrl;
    });
    return images;
  }, [items]);

  const showCategoryComingSoon =
    !itemsLoading &&
    selectedCategory !== ALL_CATEGORIES_LABEL &&
    !searchQuery.trim() &&
    filteredItems.length === 0;

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setDisplayedItemsCount(8);
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setSelectedCategory(ALL_CATEGORIES_LABEL);
    setDisplayedItemsCount(8);
  };

  // Lazy loading function
  const loadMoreItems = () => {
    if (isLoadingMore) return;

    setIsLoadingMore(true);
    setTimeout(() => {
      setDisplayedItemsCount((prev) => prev + 8);
      setIsLoadingMore(false);
    }, 500);
  };

  // Scroll handler for lazy loading
  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop !==
        document.documentElement.offsetHeight
      ) {
        return;
      }

      const filteredItems = getFilteredItems();
      if (displayedItemsCount < filteredItems.length) {
        loadMoreItems();
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [displayedItemsCount]);

  // Reset displayed items count when search/filter changes
  useEffect(() => {
    setDisplayedItemsCount(8);
  }, [searchQuery, selectedCategory]);

  useEffect(() => {
    fetchItems();
  }, []);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleItemClick = (item) => {
    if (!user) {
      setSelectedItemForAuth(item);
      setAuthDialog(true);
    } else {
      onAuthRequired();
    }
  };

  const handleBuyNow = (item) => {
    handleItemClick(item);
  };

  const handleAuthDialogClose = () => {
    setAuthDialog(false);
    setSelectedItemForAuth(null);
  };

  const handleSignInRequired = () => {
    setAuthDialog(false);
    onAuthRequired();
  };

  const handleSnackbarClose = () => {
    setSnackbar({ open: false, message: "" });
  };

  const handleSignOut = async () => {
    try {
      await onSignOut();
      handleClose();
    } catch (error) {
      setError("Failed to sign out: " + error.message);
    }
  };

  // Mock functions for components that need them (same as HomePage interface)
  const getCartItemCount = () => 0; // Public users have no cart
  const setCartDrawerOpen = () => {
    // For public users, redirect to sign in instead of showing cart
    onAuthRequired();
  };
  const getItemCartQuantity = () => 0;
  const addItemToCart = () => {
    // For public users, redirect to sign in instead of showing notification
    onAuthRequired();
  };
  const setSelectedItem = () => {};
  const setShowItemCheckout = () => {};
  const setShowCart = () => {
    // For public users, redirect to sign in instead of showing cart
    onAuthRequired();
  };

  // Create a mock user for NavBar when not signed in
  const navBarUser = user || {
    displayName: "Guest User",
    photoURL: null,
  };

  // Custom handleMenu for public users
  const handleNavBarMenu = user ? handleMenu : onAuthRequired;

  // Handle item request for public users - prompt to sign in
  const handleRequestItem = (itemName) => {
    onAuthRequired();
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: getBrandPageBackground(primaryBlue),
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 50%, rgba(0, 114, 206, 0.12) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 80%, rgba(11, 28, 72, 0.08) 0%, transparent 50%)
          `,
          zIndex: 0,
        },
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1 }}>
        {/* NavBar Component - Modified for public users */}
        {user ? (
          // If user is logged in, show normal NavBar
          <NavBar
            user={user}
            handleMenu={handleMenu}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            categories={categories}
            cartItemCount={getCartItemCount()}
            primaryBlue={primaryBlue}
            glassStyles={glassStyles}
            setCartDrawerOpen={setCartDrawerOpen}
            showSearch={true}
          />
        ) : (
          // If no user, show custom navbar with sign-in button
          <Box
            sx={{
              bgcolor: brandColors.navy,
              backdropFilter: "blur(12px)",
              borderRadius: 0,
              borderBottom: `2px solid ${brandColors.gold}`,
              position: "sticky",
              top: 0,
              zIndex: 1000,
              boxShadow: "0 4px 24px rgba(11, 28, 72, 0.35)",
            }}
          >
            <Container maxWidth="lg">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  py: 2,
                  px: { xs: 1, sm: 3 },
                }}
              >
                {/* Logo */}
                <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
                  <Box
                    component="img"
                    src="/infinitywhalelogo.png"
                    alt="InfinityWhale Logo"
                    sx={{
                      height: { xs: 32, sm: 40, md: 48 },
                      width: "auto",
                      objectFit: "contain",
                      transition: "all 0.3s ease",
                      filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
                      mr: { xs: 1, sm: 1.5, md: 2 },
                      cursor: "pointer",
                    }}
                    onClick={() => window.location.reload()}
                  />
                  <Typography
                    variant="h4"
                    component="div"
                    sx={{
                      fontWeight: "bold",
                      background: `linear-gradient(45deg, ${brandColors.gold}, ${brandColors.white})`,
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      textShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      fontSize: { xs: "1.25rem", sm: "1.5rem", md: "2rem" },
                      display: { xs: "none", sm: "block" },
                    }}
                  >
                    InfinityWhale
                  </Typography>
                </Box>

                {/* Search Bar for Desktop */}
                <Box
                  sx={{
                    display: { xs: "none", md: "flex" },
                    alignItems: "center",
                    gap: 2,
                    flexGrow: 1,
                    maxWidth: 600,
                    mx: 2,
                  }}
                >
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <Box
                          sx={{ mr: 1, display: "flex", alignItems: "center" }}
                        >
                          <Search sx={{ color: primaryBlue[400] }} />
                        </Box>
                      ),
                      endAdornment: searchQuery && (
                        <Box
                          sx={{ ml: 1, display: "flex", alignItems: "center" }}
                        >
                          <Button
                            size="small"
                            onClick={() => setSearchQuery("")}
                            sx={{
                              minWidth: "auto",
                              p: 0.5,
                              color: primaryBlue[400],
                              "&:hover": { bgcolor: "rgba(0, 114, 206, 0.1)" },
                            }}
                          >
                            ✕
                          </Button>
                        </Box>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        ...glassStyles,
                        bgcolor: "rgba(255, 255, 255, 0.8)",
                        "& fieldset": {
                          border: "1px solid rgba(255, 255, 255, 0.3)",
                        },
                        "&:hover fieldset": {
                          border: `1px solid ${primaryBlue[300]}`,
                        },
                        "&.Mui-focused fieldset": {
                          border: `2px solid ${primaryBlue[500]}`,
                        },
                      },
                    }}
                  />

                  <TextField
                    select
                    size="small"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    SelectProps={{ native: true }}
                    sx={{
                      minWidth: 120,
                      "& .MuiOutlinedInput-root": {
                        ...glassStyles,
                        bgcolor: "rgba(255, 255, 255, 0.8)",
                        "& fieldset": {
                          border: "1px solid rgba(255, 255, 255, 0.3)",
                        },
                        "&:hover fieldset": {
                          border: `1px solid ${primaryBlue[300]}`,
                        },
                        "&.Mui-focused fieldset": {
                          border: `2px solid ${primaryBlue[500]}`,
                        },
                      },
                    }}
                  >
                    {categories?.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </TextField>
                </Box>

                {/* Right Section */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {/* Mobile Search Button */}
                  <Box sx={{ display: { xs: "block", md: "none" } }}>
                    <Button
                      onClick={() =>
                        setSnackbar({
                          open: true,
                          message:
                            "Search feature - Please sign in for full access",
                        })
                      }
                      sx={{
                        ...glassStyles,
                        bgcolor: "rgba(255, 255, 255, 0.2)",
                        minWidth: "auto",
                        p: 1,
                        "&:hover": {
                          bgcolor: "rgba(255, 255, 255, 0.3)",
                        },
                      }}
                    >
                      <Search sx={{ color: primaryBlue[600] }} />
                    </Button>
                  </Box>

                  {/* Cart Icon */}
                  <Button
                    onClick={() =>
                      setSnackbar({
                        open: true,
                        message: "Please sign in to access cart",
                      })
                    }
                    sx={{
                      ...glassStyles,
                      bgcolor: "rgba(255, 255, 255, 0.2)",
                      minWidth: "auto",
                      p: 1,
                      "&:hover": {
                        bgcolor: "rgba(255, 255, 255, 0.3)",
                      },
                    }}
                  >
                    <Badge
                      badgeContent={0}
                      color="error"
                      sx={{
                        "& .MuiBadge-badge": {
                          fontSize: "0.75rem",
                          fontWeight: 600,
                        },
                      }}
                    >
                      <ShoppingCart sx={{ color: primaryBlue[600] }} />
                    </Badge>
                  </Button>

                  {/* Sign In Button */}
                  <Button
                    variant="contained"
                    startIcon={<Login />}
                    onClick={onAuthRequired}
                    sx={{
                      ...glassStyles,
                      bgcolor: primaryBlue[600],
                      color: "white",
                      fontWeight: 600,
                      px: { xs: 2, sm: 3 },
                      py: 1,
                      borderRadius: 2,
                      fontSize: { xs: "0.8rem", sm: "0.9rem" },
                      "&:hover": {
                        bgcolor: primaryBlue[700],
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <Box sx={{ display: { xs: "none", sm: "block" } }}>
                      Sign In
                    </Box>
                  </Button>
                </Box>
              </Box>
            </Container>
          </Box>
        )}

        <CategoryIconStrip
          selectedCategory={selectedCategory}
          onSelectCategory={handleCategorySelect}
          itemCountByCategory={itemCountByCategory}
          categoryImageOverrides={categoryImageOverrides}
          primaryBlue={primaryBlue}
        />

        {/* FloatingCartButton Component - Same as HomePage but disabled for public */}
        <FloatingCartButton
          getCartItemCount={getCartItemCount}
          setCartDrawerOpen={setCartDrawerOpen}
          primaryBlue={primaryBlue}
          glassStyles={glassStyles}
        />

        {/* Notifications Component - Same as HomePage */}
        <Notifications
          snackbar={snackbar}
          handleSnackbarClose={handleSnackbarClose}
          primaryBlue={primaryBlue}
          glassStyles={glassStyles}
        />

        {/* UserMenu Component - Same as HomePage but with limited functionality */}
        {user && (
          <UserMenu
            anchorEl={anchorEl}
            handleClose={handleClose}
            isAdmin={false} // Public users are not admin
            onAdminPanel={() => {}} // No admin panel for public
            handleSignOut={handleSignOut}
            setShowMyOrders={() => onAuthRequired()} // Redirect to auth
            setShowPackageStore={() => onAuthRequired()} // Redirect to auth
            setCartDrawerOpen={setCartDrawerOpen}
            setShowProfileSettings={() => onAuthRequired()} // Redirect to auth
            getCartItemCount={getCartItemCount}
            setDeleteDialogOpen={() => {}} // No delete for public
            deleteDialogOpen={false}
            handleDeleteAccount={() => {}}
            loading={false}
            primaryBlue={primaryBlue}
            glassStyles={glassStyles}
          />
        )}

        {/* Auth Required Dialog */}
        <Dialog
          open={authDialog}
          onClose={handleAuthDialogClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              ...glassStyles,
              bgcolor: "rgba(255, 255, 255, 0.95)",
            },
          }}
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <Login color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Sign In Required
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography gutterBottom sx={{ mb: 2 }}>
              To purchase "{selectedItemForAuth?.name}", you need to sign in to
              your account.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Don't have an account? We'll help you create one during the
              sign-in process.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button
              onClick={handleAuthDialogClose}
              sx={{
                color: primaryBlue[600],
                "&:hover": { bgcolor: `${primaryBlue[50]}` },
              }}
            >
              Continue Browsing
            </Button>
            <Button
              variant="contained"
              onClick={handleSignInRequired}
              startIcon={<Login />}
              sx={{
                bgcolor: primaryBlue[600],
                "&:hover": { bgcolor: primaryBlue[700] },
              }}
            >
              Sign In
            </Button>
          </DialogActions>
        </Dialog>

        {/* Main Content */}
        <Box sx={{ position: "relative", zIndex: 1 }}>
          {error && (
            <Container maxWidth="lg" sx={{ pt: 2 }}>
              <Alert
                severity="error"
                sx={{
                  mb: 2,
                  ...glassStyles,
                  bgcolor: "rgba(244, 67, 54, 0.9)",
                  color: "white",
                  fontWeight: 600,
                }}
              >
                {error}
              </Alert>
            </Container>
          )}

          <Container maxWidth="lg" sx={{ py: 4 }}>
            {showCategoryComingSoon ? (
              <CategoryComingSoonPanel
                categoryName={selectedCategory}
                primaryBlue={primaryBlue}
                glassStyles={glassStyles}
                onBrowseAll={() => handleCategorySelect(ALL_CATEGORIES_LABEL)}
                onNotifyInterest={(name) => handleRequestItem(name)}
              />
            ) : (
              <Box>
                <ProductGrid
                  itemsLoading={itemsLoading}
                  items={items}
                  filteredItems={filteredItems}
                  displayedItemsCount={displayedItemsCount}
                  searchQuery={searchQuery}
                  selectedCategory={selectedCategory}
                  handleClearFilters={handleClearFilters}
                  formatPrice={formatPrice}
                  getItemCartQuantity={getItemCartQuantity}
                  addItemToCart={addItemToCart}
                  setSelectedItem={setSelectedItem}
                  setShowItemCheckout={setShowItemCheckout}
                  isLoadingMore={isLoadingMore}
                  loadMoreItems={loadMoreItems}
                  primaryBlue={primaryBlue}
                  glassStyles={glassStyles}
                  cardGlassStyles={cardGlassStyles}
                  handleBuyNow={handleBuyNow}
                  modalOpen={modalOpen}
                  setModalOpen={setModalOpen}
                  selectedItemForModal={selectedItemForModal}
                  setSelectedItemForModal={setSelectedItemForModal}
                  hideHeader={true}
                  onRequestItem={handleRequestItem}
                />
              </Box>
            )}
          </Container>

          {/* Footer Component - Same as HomePage */}
          <Footer primaryBlue={primaryBlue} glassStyles={glassStyles} />
        </Box>
      </Box>
    </Box>
  );
};

export default PublicHomePage;
